<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import Button from 'dashboard/components-next/button/Button.vue';
import AgenteIaLayout from '../components/AgenteIaLayout.vue';
import SomenteLeituraOperadoMotor from '../components/SomenteLeituraOperadoMotor.vue';
import { emLinhas, CAMPOS_EM_LISTA } from '../helpers';

const { t } = useI18n();
const route = useRoute();

const TONS = ['proximo', 'profissional', 'acolhedor'];
const TAMANHOS = ['curta', 'media'];
const ESPERA_DA_ESTIMATIVA = 600;

const layout = ref(null);
const salvando = ref(false);
const resultado = ref(null);
const persona = ref(null);
const comChave = ref(true);
const form = ref({
  nome_agente: '',
  empresa: '',
  o_que_faz: '',
  objetivo: '',
  publico: '',
  como_se_apresenta: '',
  tom: 'proximo',
  tamanho_da_resposta: 'curta',
  usa_funil: true,
  descobrir_antes: '',
  sempre_fazer: '',
  nunca: '',
  chamar_pessoa: '',
});

const paraEnvio = () => {
  const montagem = { ...form.value };
  CAMPOS_EM_LISTA.forEach(campo => {
    montagem[campo] = emLinhas(form.value[campo]);
  });
  return montagem;
};

// Contador ao vivo: o backend escreve o texto com o PromptBuilder e devolve so o tamanho, para a tela nao duplicar a conta.
const estimativa = ref(null);
const erroTeto = ref(null);
const numero = valor => new Intl.NumberFormat().format(valor || 0);
const acimaDoTeto = computed(
  () => !!estimativa.value && estimativa.value.caracteres > estimativa.value.teto
);
let consulta = 0;
let espera = null;

const estimar = async () => {
  consulta += 1;
  const minha = consulta;
  try {
    const { data } = await AgenteIaAPI.previaMontagem(paraEnvio());
    if (minha === consulta) estimativa.value = data;
  } catch (e) {
    // Sem estimativa o contador some; salvar continua valendo.
  }
};

watch(
  form,
  () => {
    clearTimeout(espera);
    espera = setTimeout(estimar, ESPERA_DA_ESTIMATIVA);
  },
  { deep: true }
);
onBeforeUnmount(() => clearTimeout(espera));

const montar = async () => {
  salvando.value = true;
  erroTeto.value = null;
  try {
    const { data } = await AgenteIaAPI.montar(paraEnvio());
    resultado.value = data;
    estimativa.value = { caracteres: data.caracteres, teto: data.teto };
    persona.value = { ...persona.value, name: form.value.nome_agente };
    layout.value?.carregarStatus();
    useAlert(t('AGENTE_IA.MONTAR.SALVO'));
  } catch (e) {
    const dados = e?.response?.data;
    if (dados?.caracteres && dados?.teto) {
      // Aviso que some nao serve aqui: a pessoa precisa ver a conta enquanto corta o texto.
      erroTeto.value = { caracteres: dados.caracteres, teto: dados.teto };
      estimativa.value = erroTeto.value;
    } else {
      useAlert(dados?.error || t('AGENTE_IA.MONTAR.ERRO'));
    }
  } finally {
    salvando.value = false;
  }
};

// Volta as respostas guardadas, para ajustar sem responder tudo de novo.
const carregar = async () => {
  try {
    const [{ data: montagem }, { data: status }] = await Promise.all([
      AgenteIaAPI.montagem(),
      AgenteIaAPI.status(),
    ]);
    persona.value = status.persona;
    comChave.value = !!status.persona?.com_chave;
    Object.entries(montagem.montagem || {}).forEach(([chave, valor]) => {
      if (!(chave in form.value) || valor === null) return;
      form.value[chave] = Array.isArray(valor) ? valor.join('\n') : valor;
    });
    if (!form.value.nome_agente) {
      form.value.nome_agente = status.persona?.name || '';
    }
    estimar();
  } catch (e) {
    // Sem respostas guardadas o formulario comeca em branco.
  }
};

const trocarModo = async modo => {
  try {
    const { data } = await AgenteIaAPI.atualizarPersona(persona.value.id, {
      mode: modo,
    });
    persona.value = { ...persona.value, mode: data.mode };
    layout.value?.carregarStatus();
  } catch (e) {
    useAlert(e?.response?.data?.error || t('AGENTE_IA.MONTAR.ERRO'));
  }
};

onMounted(carregar);

const classeCampo =
  'w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10';
</script>

<template>
  <AgenteIaLayout
    ref="layout"
    :title="t('AGENTE_IA.MONTAR.TITULO')"
    :description="t('AGENTE_IA.MONTAR.DESCRICAO')"
  >
    <SomenteLeituraOperadoMotor>
      <section
        v-if="persona"
        class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak"
      >
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('AGENTE_IA.MONTAR.MODO.TITULO') }}
        </h2>
        <div class="flex gap-2">
          <Button
            v-for="modo in ['test', 'production']"
            :key="modo"
            size="sm"
            :variant="persona.mode === modo ? 'solid' : 'outline'"
            :label="
              t(`AGENTE_IA.MODO.${modo === 'test' ? 'TESTE' : 'PRODUCAO'}`)
            "
            :disabled="modo === 'production' && !comChave"
            @click="trocarModo(modo)"
          />
        </div>
        <span v-if="!comChave" class="text-xs text-n-amber-11">
          {{ t('AGENTE_IA.MONTAR.SEM_CHAVE') }}
        </span>
        <p class="text-xs text-n-slate-11">
          {{
            t(
              persona.mode === 'production'
                ? 'AGENTE_IA.MONTAR.MODO.AJUDA_PRODUCAO'
                : 'AGENTE_IA.MONTAR.MODO.AJUDA_TESTE'
            )
          }}
        </p>
      </section>

      <section
        class="flex flex-col gap-4 p-5 rounded-xl outline outline-1 outline-n-weak"
      >
        <h2
          class="flex items-center gap-2 text-base font-medium text-n-slate-12"
        >
          <span
            class="flex items-center justify-center text-xs font-medium rounded-full size-6 bg-n-blue-3 text-n-blue-11"
            >1</span
          >
          {{ t('AGENTE_IA.MONTAR.NEGOCIO.TITULO') }}
        </h2>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('AGENTE_IA.MONTAR.NEGOCIO.EMPRESA') }}
          </span>
          <input
            v-model="form.empresa"
            name="empresa"
            :class="classeCampo"
            :placeholder="t('AGENTE_IA.MONTAR.NEGOCIO.EMPRESA_EXEMPLO')"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('AGENTE_IA.MONTAR.NEGOCIO.O_QUE_FAZ') }}
          </span>
          <textarea
            v-model="form.o_que_faz"
            rows="2"
            :class="classeCampo"
            :placeholder="t('AGENTE_IA.MONTAR.NEGOCIO.O_QUE_FAZ_EXEMPLO')"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('AGENTE_IA.MONTAR.NEGOCIO.OBJETIVO') }}
          </span>
          <input
            v-model="form.objetivo"
            :class="classeCampo"
            :placeholder="t('AGENTE_IA.MONTAR.NEGOCIO.OBJETIVO_EXEMPLO')"
          />
          <span class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.MONTAR.NEGOCIO.OBJETIVO_AJUDA') }}
          </span>
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('AGENTE_IA.MONTAR.NEGOCIO.PUBLICO') }}
          </span>
          <input
            v-model="form.publico"
            :class="classeCampo"
            :placeholder="t('AGENTE_IA.MONTAR.NEGOCIO.PUBLICO_EXEMPLO')"
          />
        </label>
      </section>

      <section
        class="flex flex-col gap-4 p-5 rounded-xl outline outline-1 outline-n-weak"
      >
        <h2
          class="flex items-center gap-2 text-base font-medium text-n-slate-12"
        >
          <span
            class="flex items-center justify-center text-xs font-medium rounded-full size-6 bg-n-blue-3 text-n-blue-11"
            >2</span
          >
          {{ t('AGENTE_IA.MONTAR.FALA.TITULO') }}
        </h2>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('AGENTE_IA.MONTAR.FALA.NOME_AGENTE') }}
          </span>
          <input
            v-model="form.nome_agente"
            name="nome_agente"
            maxlength="60"
            :class="classeCampo"
            :placeholder="t('AGENTE_IA.MONTAR.FALA.NOME_AGENTE_EXEMPLO')"
          />
          <span class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.MONTAR.FALA.NOME_AGENTE_AJUDA') }}
          </span>
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">
            {{ t('AGENTE_IA.MONTAR.FALA.APRESENTACAO') }}
          </span>
          <input
            v-model="form.como_se_apresenta"
            :class="classeCampo"
            :placeholder="t('AGENTE_IA.MONTAR.FALA.APRESENTACAO_EXEMPLO')"
          />
        </label>
        <div class="grid gap-4 sm:grid-cols-2">
          <label class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-12">
              {{ t('AGENTE_IA.MONTAR.FALA.TOM') }}
            </span>
            <select v-model="form.tom" :class="classeCampo">
              <option v-for="tom in TONS" :key="tom" :value="tom">
                {{ t(`AGENTE_IA.MONTAR.FALA.TONS.${tom.toUpperCase()}`) }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-12">
              {{ t('AGENTE_IA.MONTAR.FALA.TAMANHO') }}
            </span>
            <select v-model="form.tamanho_da_resposta" :class="classeCampo">
              <option
                v-for="tamanho in TAMANHOS"
                :key="tamanho"
                :value="tamanho"
              >
                {{
                  t(`AGENTE_IA.MONTAR.FALA.TAMANHOS.${tamanho.toUpperCase()}`)
                }}
              </option>
            </select>
          </label>
        </div>
        <label class="flex items-start gap-2">
          <input v-model="form.usa_funil" type="checkbox" class="mt-1" />
          <span class="flex flex-col gap-0.5">
            <span class="text-sm text-n-slate-12">
              {{ t('AGENTE_IA.MONTAR.FALA.FUNIL') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('AGENTE_IA.MONTAR.FALA.FUNIL_AJUDA') }}
            </span>
          </span>
        </label>
      </section>

      <section
        class="flex flex-col gap-4 p-5 rounded-xl outline outline-1 outline-n-weak"
      >
        <h2
          class="flex items-center gap-2 text-base font-medium text-n-slate-12"
        >
          <span
            class="flex items-center justify-center text-xs font-medium rounded-full size-6 bg-n-blue-3 text-n-blue-11"
            >3</span
          >
          {{ t('AGENTE_IA.MONTAR.REGRAS.TITULO') }}
        </h2>
        <label
          v-for="campo in CAMPOS_EM_LISTA"
          :key="campo"
          class="flex flex-col gap-1"
        >
          <span class="text-sm text-n-slate-12">
            {{ t(`AGENTE_IA.MONTAR.REGRAS.${campo.toUpperCase()}`) }}
          </span>
          <textarea
            v-model="form[campo]"
            :name="campo"
            rows="3"
            :class="classeCampo"
            :placeholder="
              t(`AGENTE_IA.MONTAR.REGRAS.${campo.toUpperCase()}_EXEMPLO`)
            "
          />
          <span class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.MONTAR.REGRAS.UM_POR_LINHA') }}
          </span>
        </label>
      </section>

      <p
        v-if="erroTeto"
        role="alert"
        class="p-3 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        {{
          t('AGENTE_IA.MONTAR.ERRO_TETO', {
            caracteres: numero(erroTeto.caracteres),
            teto: numero(erroTeto.teto),
          })
        }}
      </p>

      <div class="flex flex-wrap items-center gap-3">
        <Button
          :label="t('AGENTE_IA.MONTAR.BOTAO')"
          :is-loading="salvando"
          @click="montar"
        />
        <span class="text-xs text-n-slate-11">
          {{ t('AGENTE_IA.MONTAR.AVISO') }}
        </span>
        <span
          v-if="estimativa"
          class="text-xs"
          :class="acimaDoTeto ? 'text-n-ruby-11' : 'text-n-slate-11'"
        >
          {{
            t('AGENTE_IA.MONTAR.CONTADOR', {
              caracteres: numero(estimativa.caracteres),
              teto: numero(estimativa.teto),
            })
          }}
        </span>
      </div>

      <section
        v-if="resultado"
        class="flex flex-col gap-2 p-4 rounded-lg bg-n-alpha-black2"
      >
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('AGENTE_IA.MONTAR.RESULTADO.TITULO') }}
        </h2>
        <p class="text-sm text-n-slate-12">{{ resultado.partes.descricao }}</p>
        <p class="text-xs text-n-slate-11">
          {{
            t('AGENTE_IA.MONTAR.RESULTADO.CONTAGEM', {
              diretrizes: resultado.partes.diretrizes.length,
              protecoes: resultado.partes.protecoes.length,
              caracteres: resultado.caracteres,
            })
          }}
        </p>
        <router-link
          :to="{
            name: 'agente_ia_prompt',
            params: { accountId: route.params.accountId },
          }"
          class="text-sm text-n-brand"
        >
          {{ t('AGENTE_IA.MONTAR.RESULTADO.VER') }}
        </router-link>
      </section>
    </SomenteLeituraOperadoMotor>
  </AgenteIaLayout>
</template>
